import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { groupSessionService, expertService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Video, Mic, MessageCircle, Users, ArrowLeft, ArrowRight,
  Clock, Loader2, Star, Search, Check, Info, Calendar,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Availability helpers (same as ExpertProfilePage) ──────────
const DAY_NAMES = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

const parseAvailability = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return {}; }
};

const getAvailableDayIndices = (availability) => {
  return DAY_NAMES.reduce((acc, day, idx) => {
    const slots = availability[day] ?? [];
    if (slots.length > 0) acc.push(idx);
    return acc;
  }, []);
};

const getTimeSlotsForDate = (date, availability) => {
  if (!date) return [];
  const dayName = DAY_NAMES[new Date(date).getDay()];
  const slots   = availability[dayName] ?? [];
  if (!slots.length) return [];
  const times = [];
  const now   = new Date();
  slots.forEach(({ start, end }) => {
    if (!start || !end) return;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let cur = sh * 60 + sm;
    const endMin = eh * 60 + em;
    while (cur + 30 <= endMin) {
      const h  = String(Math.floor(cur / 60)).padStart(2, '0');
      const m  = String(cur % 60).padStart(2, '0');
      const dt = new Date(`${date}T${h}:${m}`);
      if (dt > new Date(now.getTime() + 3600000)) times.push(`${h}:${m}`);
      cur += 30;
    }
  });
  return times;
};

const getNextAvailableDates = (availability, daysAhead = 60) => {
  const available = getAvailableDayIndices(availability);
  if (!available.length) return [];
  const dates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 1; i <= daysAhead; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    if (available.includes(d.getDay())) {
      dates.push(d.toISOString().split('T')[0]);
    }
  }
  return dates;
};

// ── Session Types ─────────────────────────────────────────────
const SESSION_TYPES = [
  { id: 'VIDEO', icon: Video,         label: 'Video Call',  desc: 'Face-to-face group video' },
  { id: 'AUDIO', icon: Mic,           label: 'Audio Call',  desc: 'Voice-only group call' },
  { id: 'CHAT',  icon: MessageCircle, label: 'Group Chat',  desc: 'Text-based group session' },
];

// ── Step indicator ────────────────────────────────────────────
const Steps = ({ current }) => {
  const steps = ['Choose Expert', 'Session Details', 'Date & Time'];
  return (
    <div className="flex items-center gap-2 mb-8">
      {steps.map((label, i) => {
        const idx   = i + 1;
        const done  = idx < current;
        const active = idx === current;
        return (
          <React.Fragment key={label}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                done   ? 'bg-forest-600 text-cream-50' :
                active ? 'bg-forest-900 text-cream-50' :
                         'bg-cream-200 text-forest-400'
              }`}>
                {done ? <Check size={13} /> : idx}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${active ? 'text-forest-900' : 'text-forest-400'}`}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-px ${idx < current ? 'bg-forest-600' : 'bg-cream-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Expert Card ───────────────────────────────────────────────
const ExpertCard = ({ expert, selected, onSelect }) => {
  const name  = `${expert.user?.firstName ?? ''} ${expert.user?.lastName ?? ''}`.trim();
  const specs = (() => { try { return Array.isArray(expert.specializations) ? expert.specializations : JSON.parse(expert.specializations || '[]'); } catch { return []; } })();
  const avail = parseAvailability(expert.availableFrom ?? expert.availability);
  const availDays = getAvailableDayIndices(avail)
    .map(i => DAY_NAMES[i].slice(0, 3).charAt(0).toUpperCase() + DAY_NAMES[i].slice(1, 3));

  return (
    <div onClick={() => onSelect(expert)}
      className={`cursor-pointer border-2 rounded-2xl p-4 transition-all hover:shadow-sm ${
        selected ? 'border-forest-700 bg-forest-50' : 'border-cream-200 bg-white hover:border-forest-300'
      }`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold flex-shrink-0 overflow-hidden">
          {expert.user?.avatar
            ? <img src={expert.user.avatar} alt={name} className="w-full h-full object-cover" />
            : `${expert.user?.firstName?.[0] ?? ''}${expert.user?.lastName?.[0] ?? ''}`}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold text-forest-900 text-sm">{name}</p>
            {selected && <Check size={15} className="text-forest-600 flex-shrink-0" />}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {expert.averageRating > 0 && (
              <span className="flex items-center gap-1 text-xs text-forest-500">
                <Star size={10} className="fill-gold-400 text-gold-400" />
                {Number(expert.averageRating).toFixed(1)}
              </span>
            )}
            {expert.yearsOfExperience > 0 && (
              <span className="text-xs text-forest-400">{expert.yearsOfExperience}yrs</span>
            )}
            {expert.hourlyRate && (
              <span className="text-xs font-medium text-forest-600">
                {expert.currency ?? 'GHS'} {expert.hourlyRate}/hr
              </span>
            )}
          </div>
          {specs.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {specs.slice(0, 2).map((s, i) => (
                <span key={i} className="text-[10px] bg-forest-50 text-forest-600 border border-forest-100 px-2 py-0.5 rounded-full">{s}</span>
              ))}
            </div>
          )}
          {availDays.length > 0 && (
            <div className="flex items-center gap-1 mt-1.5">
              <Calendar size={10} className="text-forest-400" />
              <span className="text-[10px] text-forest-400">Available: {availDays.join(', ')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const CreateGroupSessionPage = () => {
  const navigate  = useNavigate();
  const [step,    setStep]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [query,   setQuery]   = useState('');

  // Step 1 — expert selection
  const [selectedExpert,     setSelectedExpert]     = useState(null);
  const [expertAvailability, setExpertAvailability] = useState({});
  const [loadingAvail,       setLoadingAvail]       = useState(false);

  // Step 2 — session details
  const [form, setForm] = useState({
    title:       '',
    description: '',
    sessionType: 'VIDEO',
    duration:    60,
    notes:       '',
  });

  // Step 3 — date & time
  const [date,     setDate]     = useState('');
  const [time,     setTime]     = useState('');
  const [creating, setCreating] = useState(false);

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  // Load experts
  const { data: expertsData, loading: loadingExperts } = useApi(
    () => expertService.browse({ search: query, limit: 20, page: 1 }),
    [query],
  );
  // ApiResponse.paginated puts array directly in res.data.data
  const experts = Array.isArray(expertsData?.data)    ? expertsData.data
    : Array.isArray(expertsData?.experts)              ? expertsData.experts
    : Array.isArray(expertsData)                       ? expertsData : [];

  // When expert selected — fetch their availability
  useEffect(() => {
    if (!selectedExpert) return;
    setLoadingAvail(true);
    setDate('');
    setTime('');
    expertService.getAvailability(selectedExpert.id)
      .then(res => {
        const avail = res.data?.data?.availability ?? res.data?.availability ?? res.data?.data ?? {};
        setExpertAvailability(parseAvailability(avail));
      })
      .catch(() => {
        // Fallback to availableFrom on profile
        setExpertAvailability(parseAvailability(selectedExpert.availableFrom ?? {}));
      })
      .finally(() => setLoadingAvail(false));
  }, [selectedExpert?.id]);

  const parsedAvail  = expertAvailability;
  const availDates   = getNextAvailableDates(parsedAvail);
  const timeSlots    = getTimeSlotsForDate(date, parsedAvail);
  const hasAvail     = Object.values(parsedAvail).some(s => Array.isArray(s) && s.length > 0);
  const availDayNames = getAvailableDayIndices(parsedAvail)
    .map(i => DAY_NAMES[i].charAt(0).toUpperCase() + DAY_NAMES[i].slice(1));

  // Auto-fill title when expert selected
  useEffect(() => {
    if (selectedExpert && !form.title) {
      const name = `${selectedExpert.user?.firstName ?? ''} ${selectedExpert.user?.lastName ?? ''}`.trim();
      setForm(p => ({ ...p, title: `Group Session with ${name}` }));
    }
  }, [selectedExpert]);

  const handleCreate = async () => {
    if (!selectedExpert) { toast.error('Please select an expert'); return; }
    if (!form.title.trim()) { toast.error('Session title is required'); return; }
    if (!date || !time)   { toast.error('Please select a date and time'); return; }

    setCreating(true);
    try {
      const scheduledAt = new Date(`${date}T${time}`).toISOString();
      const res = await groupSessionService.create({
        title:           form.title,
        description:     form.description || undefined,
        sessionType:     form.sessionType,
        scheduledAt,
        duration:        Number(form.duration),
        expertProfileId: selectedExpert.id,
        notes:           form.notes || undefined,
      });
      const session = res.data?.data?.session ?? res.data?.session;
      toast.success('Group session created! Share the code with your group.');
      navigate(`/group-sessions/${session.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create session');
    } finally { setCreating(false); }
  };

  return (
    <DashboardLayout title="Create Group Session">
      <div className="max-w-2xl">

        <button onClick={() => step === 1 ? navigate(-1) : setStep(s => s - 1)}
          className="flex items-center gap-2 text-sm text-forest-500 hover:text-forest-900 transition-colors group mb-6">
          <ArrowLeft size={15} className="group-hover:-translate-x-1 transition-transform" />
          {step === 1 ? 'Back' : 'Previous Step'}
        </button>

        <Steps current={step} />

        {/* ── STEP 1 — Choose Expert ── */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-forest-50 border border-forest-200 rounded-2xl px-5 py-4 flex items-start gap-3">
              <Info size={16} className="text-forest-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-forest-700 leading-relaxed">
                Select an expert to host your group session. After booking, a <strong>unique join code</strong> is generated
                — share it with up to <strong>10 people</strong> to join the same session.
              </p>
            </div>

            {/* Search */}
            <form onSubmit={e => { e.preventDefault(); setQuery(search.trim()); }}
              className="flex gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest-400" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or specialization..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
              </div>
              <button type="submit"
                className="px-4 py-2 rounded-xl bg-forest-800 text-cream-50 text-xs font-medium hover:bg-forest-700 transition-colors">
                Search
              </button>
            </form>

            {/* Expert list */}
            {loadingExperts ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-forest-600" />
              </div>
            ) : experts.length === 0 ? (
              <div className="text-center py-12 text-forest-400">
                <Users size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No experts found</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {experts.map(e => (
                  <ExpertCard
                    key={e.id}
                    expert={e}
                    selected={selectedExpert?.id === e.id}
                    onSelect={setSelectedExpert}
                  />
                ))}
              </div>
            )}

            <button
              onClick={() => {
                if (!selectedExpert) { toast.error('Please select an expert first'); return; }
                setStep(2);
              }}
              className="btn-primary w-full justify-center flex items-center gap-2 mt-2">
              Continue <ArrowRight size={15} />
            </button>
          </div>
        )}

        {/* ── STEP 2 — Session Details ── */}
        {step === 2 && (
          <div className="space-y-5">

            {/* Selected expert summary */}
            <div className="bg-white border border-cream-200 rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-sm flex-shrink-0 overflow-hidden">
                {selectedExpert?.user?.avatar
                  ? <img src={selectedExpert.user.avatar} alt="" className="w-full h-full object-cover" />
                  : `${selectedExpert?.user?.firstName?.[0] ?? ''}${selectedExpert?.user?.lastName?.[0] ?? ''}`}
              </div>
              <div>
                <p className="text-sm font-semibold text-forest-900">
                  {selectedExpert?.user?.firstName} {selectedExpert?.user?.lastName}
                </p>
                <p className="text-xs text-forest-400">Selected expert</p>
              </div>
              <button onClick={() => setStep(1)} className="ml-auto text-xs text-forest-400 hover:text-forest-700">Change</button>
            </div>

            {/* Title */}
            <div className="bg-white border border-cream-200 rounded-2xl p-5 space-y-4">
              <h3 className="font-serif font-semibold text-forest-900">Session Details</h3>
              <div>
                <label className="block text-xs font-medium text-forest-700 mb-1.5">Session Title *</label>
                <input value={form.title} onChange={f('title')} required
                  placeholder="e.g. Tourism Business Planning Workshop"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
              </div>
              <div>
                <label className="block text-xs font-medium text-forest-700 mb-1.5">Description <span className="text-forest-400">(optional)</span></label>
                <textarea value={form.description} onChange={f('description')} rows={3}
                  placeholder="What will this group session cover?"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-forest-700 mb-1.5">Notes for participants <span className="text-forest-400">(optional)</span></label>
                <textarea value={form.notes} onChange={f('notes')} rows={2}
                  placeholder="Any preparation or requirements..."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none" />
              </div>
            </div>

            {/* Session type */}
            <div className="bg-white border border-cream-200 rounded-2xl p-5 space-y-3">
              <h3 className="font-serif font-semibold text-forest-900">Session Type</h3>
              <div className="grid grid-cols-3 gap-3">
                {SESSION_TYPES.map(t => (
                  <button key={t.id} type="button"
                    onClick={() => setForm(p => ({ ...p, sessionType: t.id }))}
                    className={`flex flex-col items-center gap-2 py-4 px-3 rounded-xl border-2 text-center transition-all ${
                      form.sessionType === t.id
                        ? 'border-forest-700 bg-forest-50'
                        : 'border-cream-200 hover:border-forest-300'
                    }`}>
                    <t.icon size={20} className={form.sessionType === t.id ? 'text-forest-700' : 'text-forest-400'} />
                    <div>
                      <p className={`text-xs font-semibold ${form.sessionType === t.id ? 'text-forest-900' : 'text-forest-600'}`}>{t.label}</p>
                      <p className="text-[10px] text-forest-400 mt-0.5">{t.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div className="bg-white border border-cream-200 rounded-2xl p-5">
              <h3 className="font-serif font-semibold text-forest-900 mb-3">Duration</h3>
              <div className="grid grid-cols-5 gap-2">
                {[30, 45, 60].map(d => (
                  <button key={d} type="button"
                    onClick={() => setForm(p => ({ ...p, duration: d }))}
                    className={`py-2.5 rounded-xl border-2 text-xs font-semibold transition-all ${
                      form.duration === d
                        ? 'border-forest-700 bg-forest-50 text-forest-900'
                        : 'border-cream-200 text-forest-500 hover:border-forest-300'
                    }`}>
                    {d < 60 ? `${d}m` : `${d/60}h`}
                  </button>
                ))}
              </div>
              {selectedExpert?.hourlyRate && (
                <p className="text-xs text-forest-500 mt-3">
                  Estimated cost: <strong className="text-forest-900">
                    {selectedExpert.currency ?? 'GHS'} {((selectedExpert.hourlyRate / 60) * form.duration).toFixed(2)}
                  </strong>
                </p>
              )}
            </div>

            <button onClick={() => setStep(3)}
              className="btn-primary w-full justify-center flex items-center gap-2">
              Continue <ArrowRight size={15} />
            </button>
          </div>
        )}

        {/* ── STEP 3 — Date & Time ── */}
        {step === 3 && (
          <div className="space-y-5">

            {/* Summary */}
            <div className="bg-white border border-cream-200 rounded-2xl p-4 space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-xs overflow-hidden flex-shrink-0">
                  {selectedExpert?.user?.firstName?.[0]}{selectedExpert?.user?.lastName?.[0]}
                </div>
                <div>
                  <p className="text-sm font-semibold text-forest-900">{form.title}</p>
                  <p className="text-xs text-forest-400">{form.sessionType} · {form.duration} mins</p>
                </div>
              </div>
            </div>

            {/* Loading availability */}
            {loadingAvail && (
              <div className="flex items-center gap-2 text-forest-500 py-4">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Loading expert availability...</span>
              </div>
            )}

            {!loadingAvail && (
              <div className="bg-white border border-cream-200 rounded-2xl p-5 space-y-4">
                <h3 className="font-serif font-semibold text-forest-900">Pick a Date & Time</h3>

                {/* Available days */}
                {hasAvail ? (
                  <div className="bg-forest-50 border border-forest-100 rounded-xl px-4 py-3">
                    <p className="text-xs font-medium text-forest-700 mb-1.5">Expert's available days</p>
                    <div className="flex flex-wrap gap-1.5">
                      {availDayNames.map(d => (
                        <span key={d} className="text-[11px] bg-forest-100 text-forest-700 px-2.5 py-1 rounded-full font-medium">{d}</span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
                    ⚠️ This expert hasn't set availability yet. You can still request a session.
                  </div>
                )}

                {/* Date picker */}
                <div>
                  <label className="block text-xs font-medium text-forest-700 mb-1.5">
                    Select Date {hasAvail && <span className="text-forest-400">(available days only)</span>}
                  </label>
                  {hasAvail ? (
                    <select value={date} onChange={e => { setDate(e.target.value); setTime(''); }} required
                      className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white">
                      <option value="">Choose a date...</option>
                      {availDates.slice(0, 30).map(d => {
                        const dt = new Date(d + 'T00:00:00');
                        return (
                          <option key={d} value={d}>
                            {dt.toLocaleDateString('en-GH', { weekday: 'long', day: 'numeric', month: 'short' })}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <input type="date" value={date}
                      onChange={e => { setDate(e.target.value); setTime(''); }}
                      min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
                  )}
                </div>

                {/* Time picker */}
                <div>
                  <label className="block text-xs font-medium text-forest-700 mb-1.5">
                    Select Time {date && hasAvail && <span className="text-forest-400">(within expert's hours)</span>}
                  </label>
                  {hasAvail ? (
                    <>
                      <select value={time} onChange={e => setTime(e.target.value)}
                        disabled={!date || timeSlots.length === 0}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white disabled:opacity-50">
                        <option value="">{!date ? 'Select a date first...' : timeSlots.length === 0 ? 'No slots available' : 'Choose a time...'}</option>
                        {timeSlots.map(t => {
                          const [h, m] = t.split(':').map(Number);
                          const ampm = h >= 12 ? 'PM' : 'AM';
                          const h12  = h % 12 || 12;
                          return <option key={t} value={t}>{h12}:{String(m).padStart(2,'0')} {ampm}</option>;
                        })}
                      </select>
                      {date && timeSlots.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">No available slots for this date. Please select another day.</p>
                      )}
                    </>
                  ) : (
                    <input type="time" value={time} onChange={e => setTime(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
                  )}
                </div>
              </div>
            )}

            {/* Info */}
            <div className="flex items-start gap-3 bg-forest-50 border border-forest-200 rounded-2xl px-5 py-4">
              <Info size={15} className="text-forest-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-forest-700 leading-relaxed">
                Once created, you'll get a <strong>unique join code</strong> (e.g. TOUR-4829).
                Share it with up to <strong>10 people</strong> — they join using the code inside TourEnt Hub.
              </p>
            </div>

            <button onClick={handleCreate} disabled={creating || !date || !time}
              className="btn-primary w-full justify-center flex items-center gap-2 text-base py-4 disabled:opacity-60">
              {creating
                ? <><Loader2 size={16} className="animate-spin" /> Creating Session...</>
                : <><Users size={16} /> Create Group Session & Get Join Code</>}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CreateGroupSessionPage;